import { useParams } from "react-router-dom";
import DatasetTableResponse from "datasets/pages/tables/types/interfaces/table";
import queryString from "query-string";
import { KeyedMutator, SWRResponse } from "swr";
import { NumberParam, useQueryParams, withDefault } from "use-query-params";
import { useDebounce } from "~/hooks/useDebounce";
import { useFetcher } from "~/hooks/useFetcher";
import SearchResponse, { TableEntity } from "~/types/interfaces/catalogResponse";

const { VITE_CATALOG_API } = import.meta.env;

export const PAGE_SIZE = 10;

const adaptHit = ({ _source }: { _source: TableEntity }): DatasetTableResponse => ({
  name: _source.name,
  description: _source.description ?? "",
  fullyQualifiedName: _source.fullyQualifiedName,
  database_schema: _source.databaseSchema.name,
  tableType: _source.tableType,
});

// Shape of a single item from GET /api/catalog/v1/datasets/{id}/tables, which is
// always up-to-date for a given dataset. The v1 payload uses snake_case field names
// with no serialization aliases, so `fqn`/`table_type` arrive as-is (not
// `fullyQualifiedName`/`tableType`). Only the fields the adapter reads are declared here.
interface DatasetTableItem {
  name: string;
  description?: string;
  fqn: string;
  database_schema: string;
  table_type?: string;
}

interface DatasetTableListResponse {
  metadata: { total_count: number };
  items: DatasetTableItem[];
}

const adaptItem = (item: DatasetTableItem): DatasetTableResponse => ({
  name: item.name,
  description: item.description ?? "",
  fullyQualifiedName: item.fqn,
  database_schema: item.database_schema,
  tableType: item.table_type,
});

// Select the adapter by the shape of the data payload: with `keepPreviousData`, the
// previous branch's payload can linger while the SWR key switches, so for a period the
// payload shape may not match the query. The v1 list response carries `items`; the
// search response carries `hits`.
const adaptResponse = (
  data: SearchResponse<TableEntity> | DatasetTableListResponse | undefined
): DatasetTableResponse[] | undefined => {
  if (!data) {
    return undefined;
  }
  return "items" in data ? data.items.map(adaptItem) : data.hits.hits.map(adaptHit);
};

const useDatasetTables = (
  searchTerm: string = "",
  id?: string
): SWRResponse<DatasetTableResponse[]> & { totalCount?: number } => {
  const { datasetId = id } = useParams();
  const debouncedSearch = useDebounce(searchTerm);
  const [query] = useQueryParams({
    perPage: withDefault(NumberParam, PAGE_SIZE),
    page: withDefault(NumberParam, 0),
  });
  const { page, perPage } = query;
  const offset = page ? (page - 1) * perPage : 0;

  const searchQs = queryString.stringify({
    index: "catalog-api-table_search_index",
    dataset_id: datasetId,
    q: debouncedSearch,
    query_filter: JSON.stringify({ query: { match_all: {} } }),
    from: offset,
    size: perPage,
  });

  // No search term: read the always-current Postgres endpoint so freshly created
  // or removed tables show up immediately, side-stepping the OpenSearch index lag.
  // When a search term is present, use the OpenSearch index for its full-text search capabilities.
  // The OpenSearch index is eventually consistent, with up to 5-minute lag as of this writing.
  const listQs = queryString.stringify({
    offset,
    limit: perPage,
    sort: "name",
  });

  const key = datasetId
    ? debouncedSearch
      ? `${VITE_CATALOG_API}/search/query?${searchQs}`
      : `${VITE_CATALOG_API}/v1/datasets/${datasetId}/tables?${listQs}`
    : null;

  const resp = useFetcher<SearchResponse<TableEntity> | DatasetTableListResponse>(key, {
    keepPreviousData: true,
  });

  const totalCount = resp.data
    ? "items" in resp.data
      ? resp.data.metadata.total_count
      : resp.data.hits.total.value
    : undefined;

  return {
    ...resp,
    data: adaptResponse(resp.data),
    totalCount,
    isLoading: resp.isLoading || Boolean(searchTerm && searchTerm !== debouncedSearch),
    mutate: resp.mutate as KeyedMutator<any>,
  } as SWRResponse<DatasetTableResponse[]> & { totalCount?: number };
};

export default useDatasetTables;
